import React from 'react'
import Moment from 'react-moment'
import { ETLProgressUpdate, ETLCurrentRun } from 'types'

interface ETLProgressCardProps {
  liveProgress: ETLProgressUpdate | null
  currentRuns: ETLCurrentRun[]
  activeRunCount: number
  wsConnected: boolean
  onTriggerETL: () => void
  triggerLoading: boolean
}

const ETLProgressCard: React.FC<ETLProgressCardProps> = ({
  liveProgress,
  currentRuns,
  activeRunCount,
  wsConnected,
  onTriggerETL,
  triggerLoading,
}) => {
  const isRunning = liveProgress?.running || activeRunCount > 0
  const progress = liveProgress?.points || 0
  const description = liveProgress?.description || 'Idle'
  const errors = liveProgress?.errors || []
  const hasErrors = errors.length > 0

  // For single run, use existing logic
  const currentRun = currentRuns.length === 1 ? currentRuns[0] : null
  const stepsCompleted = currentRun?.total_steps_completed || 0
  const totalSteps = currentRun?.total_steps || 0
  const currentStep = currentRun?.current_step

  // Calculate average progress for multiple runs
  const avgProgress = currentRuns.length > 0
    ? currentRuns.reduce((sum, run) => sum + (run.progress?.points || 0), 0) / currentRuns.length
    : progress

  return (
    <div className="etl-progress-card">
      <div className="card-header">
        <div className="header-left">
          <h3>Current Status</h3>
          {isRunning && (
            <div className="live-indicator">
              <span className="live-dot"></span>
              <span className="live-text">Live</span>
            </div>
          )}
        </div>
        {!wsConnected && isRunning && (
          <div className="connection-warning">
            <span className="material-icons">warning</span>
            <span>Reconnecting...</span>
          </div>
        )}
      </div>

      <div className="card-body">
        {isRunning ? (
          <>
            {/* Consolidated view for multiple runs */}
            {activeRunCount > 1 ? (
              <>
                <div className="progress-section">
                  <div className="progress-header">
                    <div className="progress-info">
                      <span className="material-icons sync-icon">sync</span>
                      <span className="progress-label">Processing {activeRunCount} ETL Jobs</span>
                    </div>
                    <span className="progress-percentage">{Math.round(avgProgress)}%</span>
                  </div>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${avgProgress}%` }}
                    ></div>
                  </div>
                </div>

                <div className="running-datasets-section">
                  {currentRuns.map((run, index) => (
                    <div key={run.run_id || index} className="dataset-item">
                      <div className="dataset-header">
                        <span className="material-icons dataset-icon">description</span>
                        <span className="dataset-name">Dataset {index + 1}</span>
                        <span className="dataset-progress">{Math.round(run.progress?.points || 0)}%</span>
                      </div>
                      {run.current_step && (
                        <div className="dataset-step">
                          <span className="step-name">{run.current_step.step}</span>
                        </div>
                      )}
                      <div className="dataset-progress-bar">
                        <div
                          className="progress-bar-fill small"
                          style={{ width: `${run.progress?.points || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              /* Single run view */
              <>
                <div className="progress-section">
                  <div className="progress-header">
                    <div className="progress-info">
                      <span className="material-icons sync-icon">sync</span>
                      <span className="progress-label">{description}</span>
                    </div>
                    <span className="progress-percentage">{Math.round(progress)}%</span>
                  </div>
                  <div className="progress-bar-container">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>

                {currentStep && (
                  <div className="current-step-details">
                    <div className="step-info">
                      <span className="step-label">Current Step:</span>
                      <span className="step-name">{currentStep.step}</span>
                    </div>
                    <div className="step-timing">
                      <span className="timing-label">Started</span>
                      <Moment fromNow>{currentStep.started_at}</Moment>
                    </div>
                  </div>
                )}

                {totalSteps > 0 && (
                  <div className="steps-counter">
                    <span className="material-icons">check_circle</span>
                    <span>
                      {stepsCompleted} of {totalSteps} steps completed
                    </span>
                  </div>
                )}

                {hasErrors && (
                  <div className="errors-section">
                    <div className="errors-header">
                      <span className="material-icons">error</span>
                      <span className="errors-title">Errors Detected</span>
                    </div>
                    <div className="errors-list">
                      {errors.map((error, index) => (
                        <div key={index} className="error-item">
                          {error.code && <span className="error-code">Code {error.code}:</span>}
                          <span className="error-description">{error.description}</span>
                          {error.step && <span className="error-step">in {error.step}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {currentRun && (
                  <div className="run-started-time">
                    Started{' '}
                    <Moment fromNow>{currentRun.started_at}</Moment>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div className="idle-state">
            <div className="idle-icon">
              <span className="material-icons">cloud_done</span>
            </div>
            <h4>No ETL in progress</h4>
            <p>Click the button below to start ETL now</p>
            <button
              onClick={onTriggerETL}
              disabled={triggerLoading}
              className="trigger-etl-button"
            >
              {triggerLoading ? (
                <>
                  <span className="material-icons sync-icon">sync</span>
                  Starting...
                </>
              ) : (
                <>
                  <span className="material-icons">play_arrow</span>
                  Run ETL
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ETLProgressCard
